import { useMemo } from "react";
import { useHighestApr } from "@/hooks/apr";
import {
  useDuneSupplyLocked,
  useHolderStats,
  useMarqueeDeposits,
  useMarqueeItems,
} from "@/hooks/data";
import { useTotalBurned } from "@/hooks/incinerator";

export const formatCompactNumber = (num: number): string => {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
  return num.toFixed(0);
};

export const useArcadeData = () => {
  const { highestApr, isLoading: isLoadingApr } = useHighestApr();
  const { supplyLocked, uniqueDepositors, isLoading: isLoadingSupply } = useDuneSupplyLocked();
  const { data: totalBurnedData, isLoading: isLoadingBurn } = useTotalBurned();
  const { data: holderStats, isLoading: isLoadingHolders } = useHolderStats();

  // Only expose APY strings when APR data has fully loaded and is > 0.
  // Avoid hard-coded placeholders to prevent misleading users.
  const vaultApy = useMemo<string | undefined>(() => {
    if (!isLoadingApr && highestApr > 0) return `${(highestApr * 0.8).toFixed(1)}%`;
    return undefined;
  }, [highestApr, isLoadingApr]);

  const lockApy = useMemo<string | undefined>(() => {
    if (!isLoadingApr && highestApr > 0) return `${highestApr.toFixed(1)}%`;
    return undefined;
  }, [highestApr, isLoadingApr]);

  const displayBurned = useMemo(() => {
    const totalBurnedBigInt = BigInt(totalBurnedData?.totalBurned || "0");
    const totalBurnedFormatted = Number(totalBurnedBigInt) / 1e18;
    return formatCompactNumber(totalBurnedFormatted);
  }, [totalBurnedData]);

  const isLoading = isLoadingApr || isLoadingSupply || isLoadingBurn || isLoadingHolders;

  return {
    vaultApy,
    lockApy,
    highestApr,
    isLoadingApr,
    supplyLocked,
    uniqueDepositors,
    isLoadingSupply,
    displayBurned,
    totalBurnedData,
    isLoadingBurn,
    holderStats,
    isLoadingHolders,
    isLoading,
  };
};

export const useArcadeMarquee = () => {
  const { deposits: initialDeposits, loading: depositsLoading } = useMarqueeDeposits();
  const marqueeItemsRef = useMarqueeItems(initialDeposits);

  const stableMarqueeItems = useMemo(() => {
    if (marqueeItemsRef.current.length === 0) return [];

    return marqueeItemsRef.current.map((deposit, idx) => ({
      ...deposit,
      stableKey: `${deposit.address}-${deposit.timestamp}-${idx}`,
    }));
  }, [marqueeItemsRef.current]);

  const duplicatedMarqueeItems = useMemo(() => {
    return [
      ...stableMarqueeItems,
      ...stableMarqueeItems.map((item) => ({
        ...item,
        stableKey: `duplicate-${item.stableKey}`,
      })),
    ];
  }, [stableMarqueeItems]);

  return {
    stableMarqueeItems,
    duplicatedMarqueeItems,
    depositsLoading,
    marqueeItemsRef,
  };
};

export const getStrikeColor = (strike: string | number, rounds?: { strike: bigint }[]) => {
  const STRIKE_COLORS = ["#808080", "#964DFF", "#4578FF", "#1CC75E", "#F3C734", "#D51A35"] as const;
  const strikeStr = strike.toString();
  const roundIndex = rounds?.findIndex((round) => round.strike.toString() === strikeStr) ?? -1;
  const colorIndex = roundIndex !== -1 ? roundIndex % STRIKE_COLORS.length : 0;
  return STRIKE_COLORS[colorIndex];
};
